import { useState, useEffect } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { useAuth } from '../contexts/AuthContext';
import "./Card.css"; // Keep the CSS import
import checkIcon from "../assets/icon-check.svg";
import crossIcon from "../assets/icon-cross.svg";

export default function Card({ onRangeChange }) {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport on component mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const arrayCredits = [
    "5K credits",
    "10K credits",
    "60K credits",
    "250K credits",
    "800K credits"
  ];
  const arrayDurations = [
    "~5 minutes of Audio",
    "~10 minutes of Audio",
    "~60 minutes of Audio",
    "~250 minutes of Audio",
    "~800 minutes of Audio"
  ];
  const arrayValores = [65, 109, 449, 1149, 2999];
  const [rangeValue, setRangeValue] = useState(2);

  useEffect(() => {
    // Pass the actual price value instead of range index
    onRangeChange?.(arrayValores[rangeValue]);
  }, [rangeValue, onRangeChange, arrayValores]);

  // Calculate original price (before discount)
  const getOriginalPrice = (index) => {
    return (arrayValores[index] / 0.5).toFixed(2);
  };
  
  // Handle payment process when Buy Now is clicked
  const handlePayment = async () => {
    if (!user) {
      // Use alert instead of setting error state
      alert("You must be logged in to make a purchase.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Get user details from Firebase auth context
      const { email, displayName } = user;
      
      // Send request to your backend API
      const response = await fetch('https://initiatepayment.azurewebsites.net/api/initiate_payment?code=4qEvYy0IrI3sOoSFaVeeagh1FghERbDnCf7DgqjbEHIBAzFuBqQkFA==', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          name: displayName,
          amount: arrayValores[rangeValue],
          
          // Add any other required fields for your payment processing
        }),
      });

      const data = await response.json();

      if (response.status === 200 && data.payment_url) {
        // Redirect user to payment page
        window.location.href = data.payment_url;
      } else {
        // Use alert instead of setting error state
        alert("Error initiating payment. Please try again.");
        setIsProcessing(false);
      }
    } catch (err) {
      console.error("Payment initialization error:", err);
      // Use alert instead of setting error state
      alert("Error initiating payment. Please try again.");
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="Container_card" style={{ height: "auto", minHeight: isMobile ? "min(350px, 65%)" : "min(400px, 65%)" }}>
      <div className="Container_card_top" style={{ paddingTop: isMobile ? "0.5rem" : "1rem" }}>
        <div className="credits-info">
          <h3 className="credits-amount">{arrayCredits[rangeValue]}</h3>
          <p className="credits-duration" style={{ marginTop: isMobile ? "0.1rem" : "0.25rem" }}>{arrayDurations[rangeValue]}</p>
        </div>
        <div className="Container_card_top_variable">
          <div className="price-container">
            <span className="original-price">₹{getOriginalPrice(rangeValue)}</span>
            <span id="Container_card_top_variable_value">
              ₹{arrayValores[rangeValue].toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <div className="Container_card_range" style={{ margin: isMobile ? "0.25rem 0.5rem 0.5rem 0.5rem" : "0.5rem 0.5rem 1rem 0.5rem" }}>
        <input
          value={rangeValue + 1}
          min="1"
          max="5"
          step="1"
          type="range"
          style={{
            background: `linear-gradient(90deg, #178A94 ${
              25 * rangeValue
            }%, #2C2F30 ${25 * rangeValue}%)`,
            height: "8px" // Make slider thinner on mobile
          }}
          onChange={({ target }) => setRangeValue(parseInt(target.value) - 1)}
        />
      </div>

      <div className="Container_card_switch" style={{
        margin: isMobile ? "0 0.5rem 0.75rem 0.5rem" : "0 0.5rem 1.5rem 0.5rem",
        paddingBottom: isMobile ? "0.5rem" : "1rem"
      }}>
        <span style={{ fontSize: "0.7rem" }}> <strong>Credits Validity: 3 Months</strong></span>
        <span id="Container_card_switch_discount" style={{ width: "auto", fontSize: "0.7rem", padding: "0.2rem 0.4rem" }}>50% OFF Applied</span>
      </div>

      <hr style={{ margin: "0 0.5rem" }}></hr>

      <div className="Container_card_bottom" style={{ paddingTop: isMobile ? "0.5rem" : "1rem", paddingBottom: isMobile ? "0.5rem" : "1rem" }}>
        <div className="Container_card_bottom_textLines" style={{ width: "100%" }}>
          <ul
            className="Container_card_bottom_textLines_ul"
            style={{
              listStyleType: "none",
              margin: "0",
              paddingLeft: "0.5rem",
              display: isMobile ? "grid" : "block",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr",
              gap: isMobile ? "0.25rem" : "0",
              marginBottom: isMobile ? "0.25rem" : "0"
            }}
          >
            <li style={{
              marginBottom: isMobile ? "0.1rem" : "0.35rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}>
              <img src={checkIcon} alt="Check" style={{ width: isMobile ? "14px" : "auto" }} />
              <span style={{ fontSize: "0.7rem" }}>Unlimited Downloads</span>
            </li>
            <li style={{
              marginBottom: isMobile ? "0.1rem" : "0.35rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}>
              <img src={checkIcon} alt="Check" style={{ width: isMobile ? "14px" : "auto" }} />
              <span style={{ fontSize: "0.7rem" }}>100% data ownership</span>
            </li>
            <li style={{
              marginBottom: isMobile ? "0.1rem" : "0.35rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}>
              <img src={checkIcon} alt="Check" style={{ width: isMobile ? "14px" : "auto" }} />
              <span style={{ fontSize: "0.7rem" }}>24/7 Support</span>
            </li>
            <li style={{
                marginBottom: isMobile ? "0.1rem" : "0.35rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem"
              }}>
                <img src={checkIcon} alt="Check" style={{ width: isMobile ? "14px" : "auto" }} />
                <span style={{ fontSize: "0.7rem" }}>
                  Voice Cloning Included
                </span>
              </li>
          </ul>
        </div>

        {/* Buy Now button with loading state */}
        <button
          className="Container_card_bottom_button"
          style={{
            marginTop: isMobile ? "0.5rem" : "0.75rem",
            height: isMobile ? "32px" : "auto",
            paddingBlock: isMobile ? "0.25rem" : "0.5rem",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            opacity: isProcessing ? 0.7 : 1,
            cursor: isProcessing ? 'not-allowed' : 'pointer'
          }}
          onClick={handlePayment}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Loading
            </>
          ) : (
            "Buy Now"
          )}
        </button>

        {/* User info and legal billing entity */}
        <div style={{
          fontSize: '0.65rem',
          color: '#B0B4BB',
          textAlign: 'center',
          marginTop: isMobile ? "0.2rem" : "0.25rem",
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? "0.1rem" : "0.15rem"
        }}>
          {user && (
            <span><strong>Purchasing as:</strong> <strong>{user.displayName || 'User'}</strong> (<strong>{user.email}</strong>)</span>
          )}
          <span><strong>Payment to:</strong> <strong>VOQAI LABS PRIVATE LIMITED</strong></span>
        </div>
      </div>
    </div>
  );
}